import { useState, useEffect, useCallback } from "react";

// Soft "Add to Home Screen" helper. Detects the platform so we can show the
// right nudge: a real install button on Chrome/Edge, manual instructions on
// iOS, and an "open in your browser" hint inside in-app browsers (Instagram,
// Facebook, etc.) where installing is impossible.

const DISMISS_KEY = "brill_install_dismissed";

// ── Module-level capture ──────────────────────────────────────────────────
// `beforeinstallprompt` fires once, early — often before a given component
// (e.g. the Settings card, which only mounts when Settings is opened) has
// mounted. We capture it at module scope and notify subscribers, so any
// component that mounts later still receives the saved event and can show a
// real install button instead of falling back to manual instructions.
let cachedPrompt = null;
let installedFlag = false;
const subscribers = new Set();

function notify() {
  subscribers.forEach((fn) => fn());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Stop Chrome's automatic mini-infobar; we surface our own UI instead.
    e.preventDefault();
    cachedPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    cachedPrompt = null;
    installedFlag = true;
    try { localStorage.removeItem(DISMISS_KEY); } catch (err) { /* ignore */ }
    notify();
  });

  // Detect an install from a previous session while viewing in a normal browser
  // tab (where display-mode is not "standalone").
  refreshInstalledState();
}

// Re-query whether our PWA is installed. Chromium-only (getInstalledRelatedApps);
// the manifest declares a self-referencing related "webapp", so a match means
// we're installed. Updates in both directions, so an uninstall (or a stale
// detection) is corrected when called again. Resolves to the latest boolean.
function refreshInstalledState() {
  if (typeof navigator === "undefined" || !navigator.getInstalledRelatedApps) {
    return Promise.resolve(installedFlag);
  }
  return navigator.getInstalledRelatedApps().then((apps) => {
    installedFlag = !!(apps && apps.some((a) => a.platform === "webapp"));
    notify();
    return installedFlag;
  }).catch(() => installedFlag);
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as "Macintosh" but has a touch screen.
  const isIOS = /iphone|ipad|ipod/i.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  // In-app browsers (social apps, embedded webviews) can't install PWAs.
  const inApp = /FBAN|FBAV|Instagram|Line\/|Twitter|Pinterest|LinkedInApp|Snapchat|GSA\//i.test(ua);
  if (isIOS) return inApp ? "ios-inapp" : "ios";
  if (inApp) return "android-inapp";
  return "other";
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true // iOS Safari
  );
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(cachedPrompt);
  const [installed, setInstalled] = useState(() => isStandalone() || installedFlag);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "true"
  );
  const platform = detectPlatform();

  useEffect(() => {
    const sync = () => {
      setDeferredPrompt(cachedPrompt);
      setInstalled(installedFlag || isStandalone());
    };
    subscribers.add(sync);
    sync(); // pick up anything captured before this effect attached
    return () => { subscribers.delete(sync); };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!cachedPrompt) return;
    cachedPrompt.prompt();
    try {
      const choice = await cachedPrompt.userChoice;
      if (choice && choice.outcome === "accepted") setInstalled(true);
    } catch (err) {
      // user dismissed the native dialog; nothing to do
    }
    // The prompt can only be used once.
    cachedPrompt = null;
    notify();
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  }, []);

  const recheck = useCallback(() => refreshInstalledState(), []);

  return { deferredPrompt, installed, dismissed, dismiss, promptInstall, platform, recheck };
}
